import React, { useEffect, useMemo, useState } from 'react';

import '@aft/support-center/styles.css';

import Icon from 'views/site/Icon.js';
import Modal from 'views/site/Modal.js';
import Services from '@aft/support-center/Services.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';
import Tree from '@aft/support-center/Tree.js';
import omnishield from '@aft/support-center/trees/omnishield.js';

/*
 * SupportFlowModal
 *
 * The OmniShield support flow, inside this site.
 *
 * Nothing about the questions, the articles, the escalation or the assistant is
 * written here. It all comes out of src/support, which is the same code the
 * Support Center runs, synced rather than copied by hand. This file is the
 * whole integration: name the dealership, hand over the colors, and give the
 * flow a frame to sit in.
 *
 * THE DEALERSHIP IS WHAT MAKES IT THEIRS. Passing it in means the call screen
 * offers this dealer's number rather than the home office, escalations route to
 * them without a lookup, the submitted request carries their id, and GRACI is
 * narrowed to the products they actually sell. See support/Services.js.
 */

// The brand entry the Support Center keeps in its registry. This site only ever
// shows OmniShield, so it is stated here rather than looked up.
const BRAND = {
    ...omnishield,
    assistant: true,
    escalation: 'dealership_then_home_office',
    id: 'omnishield',
    legacy: false,
    name: 'OmniShield',
    notice: null
};

// The two header actions share one quiet treatment. Neither is primary: the
// question on the step is what the customer is meant to be answering.
const action = () => ({
    alignItems: 'center',
    backgroundColor: 'transparent',
    color: Theme.colors.subText(),
    cursor: 'pointer',
    display: 'inline-flex',
    fontFamily: Theme.fonts.body,
    fontSize: 13,
    fontWeight: 600,
    gap: 6,
    padding: '6px 2px',
    whiteSpace: 'nowrap'
});

const SupportFlowModal = ({ dealership, entry, onClose, open }) => {

    const mobile = Utils.isMobile();

    // Bumped to restart the flow, which rebuilds the Tree rather than trying to
    // unpick a half finished run.
    const [generation, setGeneration] = useState(0);

    /*
     * DURING render, not in an effect.
     *
     * Every screen in the package reads its colors while it renders, so
     * configuring after paint leaves the first frame on the fallback blue with
     * nothing to trigger a correction. The accent then only appears where some
     * local state happens to change, which looks like buttons turning the right
     * color when you hover them.
     */
    useMemo(() => {
        if(!dealership) {
            return;
        }
        /*
         * The same `colors` object App.js already hands to Brand.set, taken
         * from the dealership preferences rather than reconstructed from
         * guessed field names. The package derives everything else from the
         * primary, including the contrast corrections that keep white text
         * legible on whatever this dealer chose.
         */
        Services.configure({
            colors: dealership.colors || {},
            dealership: {
                address: dealership.address,
                id: dealership.id,
                name: dealership.name,
                phone_number: dealership.phone_number
            }
        });
    }, [dealership]);

    /*
     * Reset after the closing animation, not during it.
     *
     * Somebody who closed the dialog partway through and came back later meant
     * to begin again rather than resume, so the flow does reset. Where that
     * happens is the whole problem: this modal is rendered BY Tree, so bumping
     * the key unmounts the dialog along with the flow.
     *
     * Reset on open and it mounts already open, with no closed state to fade in
     * from. Reset the instant it closes and it is torn out of the DOM before
     * the fade out can run. Both were tried and both looked like the animation
     * was missing.
     *
     * So it waits for the transition to finish, then resets while nothing is on
     * screen. The delay is the modal's own 0.3s plus a little.
     */
    useEffect(() => {

        if(open) {
            return;
        }

        let timer = setTimeout(() => setGeneration(current => current + 1), 400);
        return () => clearTimeout(timer);
    }, [open]);

    /*
     * The header IS the flow's action bar.
     *
     * Which means the chrome has to be able to reach it, and the first attempt
     * could not: the bar was rendered inside the modal body while the header
     * sat above it, empty, still drawing its own padding and border. An empty
     * strip with a close button floating in it.
     *
     * So `chrome` returns the Modal rather than rendering into one. Tree calls
     * it with the actions and the current step, and this builds the dialog
     * around both. The bar lands in the header, which does not scroll, so Back
     * and the way out stay reachable partway down a long article.
     */
    const bar = ({ canGoBack, onBack, onContact, onRestart }) => (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            gap: 14,
            justifyContent: 'space-between',
            minHeight: 30,
            width: '100%'
        }}>
            <span style={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
                {canGoBack && (
                    <button className={'site-link'} onClick={onBack} style={action()}>
                        <Icon name={'arrow-right'} size={10} style={{ transform: 'rotate(180deg)' }} />
                        {'Back'}
                    </button>
                )}
            </span>

            <span style={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 12 }}>
                {onContact && (
                    <>
                        <button className={'site-link'} onClick={onContact} style={action()}>
                            <Icon name={'people-fill'} size={12} />
                            {mobile ? 'Help' : 'Talk to a person'}
                        </button>
                        <span style={{ backgroundColor: Theme.colors.border(), height: 14, width: 1 }} />
                    </>
                )}
                <button className={'site-link'} onClick={onRestart} style={action()}>
                    {'Start over'}
                </button>
            </span>
        </div>
    );

    return (
        <Tree
        key={`${entry || 'top'}:${generation}`}
        brand={BRAND}
        /*
         * Where the flow opens. Normally the top; a legacy link that used to
         * point at its own page opens on the questions that replaced it. Part
         * of the key so arriving from a different link restarts rather than
         * resuming somewhere unrelated.
         */
        entry={entry}
        chrome={({ canGoBack, children, missing, onBack, onContact, onRestart }) => (
            <Modal
            actions={bar({ canGoBack, onBack, onContact, onRestart })}
            label={'OmniShield support'}
            onClose={onClose}
            open={open}
            width={760}>
                {missing ? (
                    <span style={Theme.textStyles.body()}>
                        {'Support for this system is not available here yet.'}
                    </span>
                ) : children}
            </Modal>
        )}
        onRestart={() => setGeneration(current => current + 1)} />
    )
}

export default SupportFlowModal;
